import {
  Database,
  FileText,
  ChevronRight,
  ArrowLeft,
  ChevronDown,
  Check,
  Video,
  Music,
  Mic,
} from "lucide-react";
import { StepStepper } from "../../index";
import { useEffect, useState } from "react";
import { workspaceService, mediaService } from "../../../../services";

const STEPS = [
  { step: 1, label: "Workspace Details", desc: "Name and Company assignment" },
  { step: 2, label: "Data Permissions", desc: "Select SQL tables, PDF & media files" },
];

const inputCls =
  "w-full px-4 py-2.5 rounded-xl border border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-900/50 text-slate-900 dark:text-white focus:bg-white dark:focus:bg-slate-800 focus:ring-2 focus:ring-[#1d57a5] outline-none transition-all text-sm";
const labelCls =
  "block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1.5";

export default function WorkspaceOnboarding({
  step,
  setStep,
  data,
  setData,
  companies,
  onFinish,
  selectedWorkspaceDetails,
}) {
  const pickValue = (obj = {}, keys = []) => {
    if (!obj || typeof obj !== "object") return undefined;
    for (const key of keys) {
      if (obj[key] !== undefined && obj[key] !== null) return obj[key];
      const foundKey = Object.keys(obj).find(
        (k) => k.toLowerCase() === String(key).toLowerCase(),
      );
      if (foundKey && obj[foundKey] !== undefined && obj[foundKey] !== null) {
        return obj[foundKey];
      }
    }
    return undefined;
  };

  const toServiceType = (value = "") => {
    const normalized = String(value).toLowerCase();
    if (normalized.includes("sql")) return "SqlAnalytics";
    if (normalized.includes("file")) return "FileAnalytics";
    if (normalized.includes("media")) return "MediaAnalytics";
    return "";
  };

  const parseUseCaseDetails = (useCaseDetails) => {
    if (Array.isArray(useCaseDetails)) return useCaseDetails;
    if (typeof useCaseDetails === "string") {
      try {
        const parsed = JSON.parse(useCaseDetails);
        return Array.isArray(parsed) ? parsed : [];
      } catch {
        return [];
      }
    }
    return [];
  };

  const normalizeTableName = (value = "") =>
    String(value)
      .trim()
      .toLowerCase()
      .replace(/[[\]`"]/g, "")
      .replace(/\s+/g, "");

  const isSameTableName = (left = "", right = "") => {
    const l = normalizeTableName(left);
    const r = normalizeTableName(right);
    if (!l || !r) return false;
    if (l === r) return true;

    const lParts = l.split(".");
    const rParts = r.split(".");
    const lLast = lParts[lParts.length - 1];
    const rLast = rParts[rParts.length - 1];
    const lLastTwo = lParts.slice(-2).join(".");
    const rLastTwo = rParts.slice(-2).join(".");

    return (
      lLast === rLast ||
      lLastTwo === rLastTwo ||
      l.endsWith(`.${rLast}`) ||
      r.endsWith(`.${lLast}`)
    );
  };

  const getNormalizedDetailName = (detail = {}) =>
    pickValue(detail, [
      "tableOrFileName",
      "tableName",
      "fileName",
      "tablesSelected",
    ]) || "";

  const getFileNameFromPath = (value = "") => {
    const parts = String(value).split(/[\\/]/);
    return parts[parts.length - 1] || "";
  };

  const isFileSelected = (file, details = []) => {
    const fileName = String(file.fileName || "").toLowerCase();
    const filePathName = getFileNameFromPath(file.filePath).toLowerCase();

    return details.some((detail) => {
      const serviceType = toServiceType(
        pickValue(detail, ["serviceType", "selectedService", "service"]),
      );
      if (serviceType !== "FileAnalytics") return false;

      const detailName = String(getNormalizedDetailName(detail)).toLowerCase();
      const detailFieldName = getFileNameFromPath(
        detail.selectedFields,
      ).toLowerCase();

      return (
        detailName === fileName ||
        detailName === filePathName ||
        detailFieldName === fileName ||
        detailFieldName === filePathName
      );
    });
  };

 const isMediaSelected = (media, details = []) => {
  // Get the media name from different possible property names
  const mediaName = String(
    media.originalFileName || media.fileName || media.name || ""
  ).toLowerCase();

  return details.some((detail) => {
    const serviceType = toServiceType(
      pickValue(detail, ["serviceType", "selectedService", "service"]),
    );
    if (serviceType !== "MediaAnalytics") return false;

    const detailName = String(getNormalizedDetailName(detail)).toLowerCase();
    
    // Log for debugging
    console.log("Comparing:", detailName, "vs", mediaName);
    
    return detailName === mediaName;
  });
};

  const getSelectedSqlTableNames = (details = []) =>
    details
      .filter(
        (detail) =>
          toServiceType(
            pickValue(detail, ["serviceType", "selectedService", "service"]),
          ) === "SqlAnalytics",
      )
      .flatMap((detail) =>
        String(getNormalizedDetailName(detail) || "")
          .split(",")
          .map((name) => name.trim())
          .filter(Boolean),
      );

  const isTableSelected = (
    tableName,
    currentSelectedTables = [],
    details = [],
  ) =>
    currentSelectedTables.some((selected) =>
      isSameTableName(selected, tableName),
    ) ||
    getSelectedSqlTableNames(details).some((selected) =>
      isSameTableName(selected, tableName),
    );

  const [availableTables, setAvailableTables] = useState([]);
  const [availableFiles, setAvailableFiles] = useState([]);
  const [availableMedia, setAvailableMedia] = useState([]);
  const [selectedTables, setSelectedTables] = useState([]);
  const [fieldsByTable, setFieldsByTable] = useState([]);

  const getFieldsForTable = (tableName) =>
    fieldsByTable.find((entry) => isSameTableName(entry.tableName, tableName))
      ?.fieldName || [];

  /* ================= INIT (EDIT MODE) ================= */
  useEffect(() => {
    if (!selectedWorkspaceDetails) return;

    const rawDetails = parseUseCaseDetails(
      selectedWorkspaceDetails.useCaseDetails ||
        selectedWorkspaceDetails.usecaseDetails ||
        selectedWorkspaceDetails.useCaseDetail ||
        selectedWorkspaceDetails.usecaseDetail,
    );

    const validDetails = rawDetails.map((d) => {
      const normalizedService = toServiceType(
        pickValue(d, ["serviceType", "selectedService", "service"]),
      );
      let parsedFields =
        pickValue(d, [
          "selectedFields",
          "selectedColumn",
          "selectedColumns",
          "fieldName",
        ]) || "";
      if (normalizedService === "SqlAnalytics") {
        if (typeof parsedFields === "string") {
          parsedFields = parsedFields
            .split(",")
            .map((f) => f.trim())
            .filter(Boolean);
        } else if (!Array.isArray(parsedFields)) {
          parsedFields = [];
        }
      } else if (Array.isArray(parsedFields)) {
        parsedFields = parsedFields.join(",");
      }

      return {
        ...d,
        selectedFields: parsedFields,
        selectedService: normalizedService,
        serviceType: normalizedService,
        tableOrFileName: getNormalizedDetailName(d),
      };
    });

    setData((d) => ({
      ...d,
      companyId:
        String(
          pickValue(selectedWorkspaceDetails, ["companyId", "companyID"]) || "",
        ) || "",
      useCaseName:
        pickValue(selectedWorkspaceDetails, ["useCaseName", "usecaseName"]) ||
        "",
      description:
        pickValue(selectedWorkspaceDetails, ["description"]) || "",

      filePromptConfiguration:
        pickValue(selectedWorkspaceDetails, [
          "filePromptConfiguration",
          "filePromptConfig",
        ]) || "",
      sqlPromptConfiguration:
        pickValue(selectedWorkspaceDetails, [
          "sqlPromptConfiguration",
          "sqlPromptConfig",
        ]) || "",
      mediaPromptConfiguration:
        pickValue(selectedWorkspaceDetails, [
          "mediaPromptConfiguration",
          "mediaPromptConfig",
        ]) || "",
      useCaseDetails: validDetails,
    }));

    const tables = getSelectedSqlTableNames(validDetails);
    setSelectedTables([...new Set(tables.map((t) => String(t).trim()))]);
  }, [selectedWorkspaceDetails]);

  /* ================= FETCH TABLES + FILES + MEDIA ================= */
  /* ================= FETCH TABLES + FILES + MEDIA ================= */
useEffect(() => {
  if (!data.companyId) return;

  const fetchData = async () => {
    try {
      const res = await workspaceService.getSQLandFileConfigByCompanyID(
        data.companyId,
      );

      if (res?.data) {
        setAvailableFiles(
          res.data
            .filter((x) => x.serviceType === "FileAnalytics")
            .map((val) => ({
              fileName: val.fileName,
              filePath: val.filePath,
            })),
        );

        const sqlData = res.data.find((x) => x.serviceType === "SqlAnalytics");
        setAvailableTables(
          sqlData?.tablesSelected?.split(",").map((val) => ({
            ...sqlData,
            tablesSelected: val.trim(),
          })) || [],
        );
      }
    } catch (error) {
      console.error("Failed to fetch SQL/File config:", error);
    }

    // Fetch media files separately
    try {
      console.log("📡 Fetching media files for company:", data.companyId);
      const mediaResponse = await mediaService.getMediaByCompanyId(data.companyId);
      console.log("📥 Media API response:", mediaResponse);
      
      // Handle different response structures
      let mediaList = [];
      if (mediaResponse?.data && Array.isArray(mediaResponse.data)) {
        mediaList = mediaResponse.data;
      } else if (Array.isArray(mediaResponse)) {
        mediaList = mediaResponse;
      }
      
      if (mediaList.length > 0) {
        console.log(`🎬 Found ${mediaList.length} media files:`, mediaList.map(m => m.originalFileName || m.fileName));
        setAvailableMedia(mediaList);
      } else {
        console.log("📭 No media files found for this company");
        setAvailableMedia([]);
      }
    } catch (error) {
      console.error("❌ Failed to fetch media files:", error);
      setAvailableMedia([]);
    }
  };

  fetchData();
}, [data.companyId]);

  /* ================= FETCH FIELDS ================= */
  useEffect(() => {
    if (!data.companyId || selectedTables.length === 0) {
      setFieldsByTable([]);
      return;
    }

    const fetchFields = async () => {
      const table = availableTables.find(
        (t) => t.tablesSelected === selectedTables[0],
      );

      if (!table) return;

      const payload = {
        companyId: data.companyId,
        databaseName: table.databaseName,
        connectionString: table.connectionString,
        selectedTables,
      };

      const fieldData = await workspaceService.getFieldsByTableName(payload);

      const updated = selectedTables.map((tbl) => ({
        tableName: tbl,
        fieldName: fieldData.data
          .filter((x) => x.tableName === tbl)
          .map((y) => y.columnName),
      }));

      setFieldsByTable(updated);
    };

    fetchFields();
  }, [selectedTables, data.companyId, availableTables]);

  useEffect(() => {
    if (selectedTables.length === 0) return;
    if (!Array.isArray(fieldsByTable) || fieldsByTable.length === 0) return;

    setData((d) => {
      const details = Array.isArray(d.useCaseDetails) ? d.useCaseDetails : [];
      let changed = false;

      const nextDetails = details.map((detail) => {
        const serviceType = toServiceType(
          pickValue(detail, ["serviceType", "selectedService", "service"]),
        );
        if (serviceType !== "SqlAnalytics") return detail;

        const tableName = getNormalizedDetailName(detail);
        const isSelected = selectedTables.some((t) =>
          isSameTableName(t, tableName),
        );
        if (!isSelected) return detail;

        const tableFields = getFieldsForTable(tableName);
        if (!Array.isArray(tableFields) || tableFields.length === 0)
          return detail;

        const currentFields = Array.isArray(detail.selectedFields)
          ? detail.selectedFields
          : [];
        if (currentFields.length > 0) return detail;

        changed = true;
        return { ...detail, selectedFields: tableFields };
      });

      selectedTables.forEach((tableName) => {
        const exists = nextDetails.some(
          (detail) =>
            toServiceType(
              pickValue(detail, ["serviceType", "selectedService", "service"]),
            ) === "SqlAnalytics" &&
            isSameTableName(getNormalizedDetailName(detail), tableName),
        );
        if (exists) return;
        const tableFields = getFieldsForTable(tableName);
        changed = true;
        nextDetails.push({
          selectedService: "SqlAnalytics",
          tableOrFileName: tableName,
          selectedFields: tableFields,
        });
      });

      return changed ? { ...d, useCaseDetails: nextDetails } : d;
    });
  }, [fieldsByTable, selectedTables]);

  const [toast, setToast] = useState({
    visible: false,
    message: "",
    type: "success",
  });
  const showToast = (message, type = "success") => {
    setToast({ visible: true, message, type });
    setTimeout(
      () => setToast({ visible: false, message: "", type: "success" }),
      2000,
    );
  };

  /* ================= TOGGLES ================= */

  const toggleTable = (table) => {
    const tableName = table.tablesSelected;
    setSelectedTables((prev) => {
      const exists = prev.some((t) => isSameTableName(t, tableName));
      return exists
        ? prev.filter((t) => !isSameTableName(t, tableName))
        : [...prev, tableName];
    });

    setData((d) => {
      const details = Array.isArray(d.useCaseDetails) ? d.useCaseDetails : [];
      const exists = details.some(
        (detail) =>
          toServiceType(
            pickValue(detail, ["serviceType", "selectedService", "service"]),
          ) === "SqlAnalytics" &&
          isSameTableName(getNormalizedDetailName(detail), tableName),
      );

      if (exists) {
        return {
          ...d,
          useCaseDetails: details.filter(
            (detail) =>
              !(
                toServiceType(
                  pickValue(detail, [
                    "serviceType",
                    "selectedService",
                    "service",
                  ]),
                ) === "SqlAnalytics" &&
                isSameTableName(getNormalizedDetailName(detail), tableName)
              ),
          ),
        };
      }

      return {
        ...d,
        useCaseDetails: [
          ...details,
          {
            selectedService: "SqlAnalytics",
            tableOrFileName: tableName,
            selectedFields: getFieldsForTable(tableName),
          },
        ],
      };
    });
  };

  const toggleFile = (file) => {
    setData((d) => {
      const exists = isFileSelected(file, d.useCaseDetails);

      const updated = exists
        ? d.useCaseDetails.filter((detail) => !isFileSelected(file, [detail]))
        : [
            ...d.useCaseDetails,
            {
              selectedService: "FileAnalytics",
              tableOrFileName: file.fileName,
              selectedFields: file.filePath,
            },
          ];

      return { ...d, useCaseDetails: updated };
    });
  };

  const toggleMedia = (media) => {
  setData((d) => {
    const mediaName = String(
      media.originalFileName || media.fileName || media.name || ""
    ).trim();
    const exists = isMediaSelected(media, d.useCaseDetails);

    console.log("Toggling media:", mediaName, "Exists:", exists);

    const updated = exists
      ? d.useCaseDetails.filter((detail) => !isMediaSelected(media, [detail]))
      : [
          ...d.useCaseDetails,
          {
            selectedService: "MediaAnalytics",
            tableOrFileName: mediaName,
            selectedFields: media.filePath || "",
            mediaId: media.id,
            mediaType: media.fileType,
          },
        ];

    return { ...d, useCaseDetails: updated };
  });
};

  console.log(data, "data");
  return (
    <div className="bg-white dark:bg-slate-800 rounded-[2.5rem] shadow-sm border border-slate-100 dark:border-slate-700 flex flex-col md:flex-row overflow-hidden animate-in fade-in slide-in-from-bottom-4 duration-500" style={{ height: 'calc(100vh - 10rem)' }}>
      {/* Left Sidebar */}
      <div className="w-full md:w-80 flex-shrink-0 bg-slate-50 dark:bg-slate-900/40 border-r border-slate-100 dark:border-slate-700 p-8 sm:p-10 flex flex-col overflow-hidden">
        <div className="mb-10">
          <h3 className="text-xl font-bold text-slate-800 dark:text-white">
            New Workspace
          </h3>
          <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
            Design tailored data access
          </p>
        </div>
        <StepStepper steps={STEPS} currentStep={step} />
      </div>

      {/* Right Content */}
      <div className="flex-1 bg-white dark:bg-slate-800 relative overflow-x-hidden overflow-y-auto flex flex-col">
        <div className="flex-1 p-8 sm:p-12 overflow-y-auto hide-scrollbar">
          {/* Step 1 */}
          {step === 1 && (
            <div className="max-w-2xl animate-in fade-in slide-in-from-right-4 duration-500 pb-20">
              <div className="mb-10">
                <h2 className="text-3xl font-bold text-slate-800 dark:text-white mb-2">
                  Workspace Details
                </h2>
                <p className="text-slate-500 dark:text-slate-400 text-sm">
                  Select the target company and provide basic details for this
                  AI workspace.
                </p>
              </div>

              <div className="space-y-8">
                <div>
                  <label className={labelCls}>
                    Target Company Tenant{" "}
                    <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <select
                      value={data.companyId}
                      onChange={(e) => {
                        setSelectedTables([]);
                        setFieldsByTable([]);
                        setData((d) => ({
                          ...d,
                          companyId: e.target.value,
                          filePromptConfiguration: "",
                          sqlPromptConfiguration: "",
                          mediaPromptConfiguration: "",
                          useCaseDetails: [],
                        }));
                      }}
                      className={
                        inputCls + " py-3 appearance-none cursor-pointer"
                      }
                    >
                      <option value="">Select a tenant company…</option>
                      {companies.map((c) => (
                        <option
                          key={String(c.companyID)}
                          value={String(c.companyID)}
                        >
                          {c.companyName}
                        </option>
                      ))}
                    </select>
                    <ChevronDown
                      size={16}
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"
                    />
                  </div>
                </div>

                {data.companyId !== "" && (
                  <>
                    <div className="animate-in fade-in duration-300 space-y-5">
                      <div>
                        <label className={labelCls}>
                          Workspace Name <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          placeholder="e.g. Finance & Accounting"
                          value={data.useCaseName}
                          onChange={(e) =>
                            setData((d) => ({
                              ...d,
                              useCaseName: e.target.value,
                            }))
                          }
                          className={inputCls}
                        />
                      </div>
                    </div>
                    <div className="col-span-2">
                      <label className={labelCls}>
                        Description{" "}
                        <span className="text-red-500">*</span>
                      </label>

                      <textarea
                        rows={4}
                        placeholder="e.g. Add description..."
                        value={data.description || ""}
                        onChange={(e) =>
                          setData((d) => ({
                            ...d,
                            description: e.target.value,
                          }))
                        }
                        className={inputCls + " resize-y"}
                      />
                    </div>
                  </>
                )}
              </div>

              <div className="absolute bottom-0 left-0 right-0 p-6 bg-white/90 dark:bg-slate-800/90 backdrop-blur-sm border-t border-slate-100 dark:border-slate-700 flex justify-end">
                <button
                  disabled={data.companyId === "" && data.useCaseName === ""}
                  onClick={() => {
                    if (!data.companyId) {
                      showToast("Please select a Company", "error");
                      return;
                    }
                    if (!String(data.useCaseName || "").trim()) {
                      showToast("Workspace Name is required", "error");
                      return;
                    }
                    setStep(2);
                  }}
                  className="bg-[#1d57a5] hover:bg-[#164280] disabled:bg-slate-300 disabled:text-slate-500 text-white px-8 py-2.5 rounded-full font-medium transition-all shadow-md hover:shadow-lg hover:-translate-y-0.5 flex items-center gap-2"
                >
                  Continue <ChevronRight size={18} />
                </button>
              </div>
            </div>
          )}

          {/* Step 2 */}
          {step === 2 && (
            <div className="max-w-3xl animate-in fade-in slide-in-from-right-4 duration-500 pb-20">
              <div className="mb-10">
                <h2 className="text-3xl font-bold text-slate-800 dark:text-white mb-2">
                  Data Permissions
                </h2>
                <p className="text-slate-500 dark:text-slate-400 text-sm">
                  Select which databases, documents, and media files users in this workspace can query.
                </p>
              </div>

              {data.companyId !== "" && (
                <div className="space-y-8">

                  {/* ── SQL Tables ── */}
                  <div>
                    <h4 className="text-sm font-bold text-slate-800 dark:text-slate-200 mb-4 pb-2 border-b border-slate-100 dark:border-slate-700 flex items-center gap-2">
                      <Database size={16} className="text-amber-500" />
                      Accessible SQL Tables
                    </h4>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {availableTables.length > 0 ? (
                        <>
                          <div className="col-span-2">
                            <label className={labelCls}>
                              SQL Prompt / Instructions <span className="text-red-500">*</span>
                            </label>
                            <textarea
                              rows={4}
                              placeholder="e.g. You are an HR assistant..."
                              value={data.sqlPromptConfiguration}
                              onChange={(e) => setData((d) => ({ ...d, sqlPromptConfiguration: e.target.value }))}
                              className={inputCls + " resize-y"}
                            />
                          </div>

                          {/* Select All row */}
                          <div className="col-span-2 flex items-center justify-between px-1">
                            <span className="text-xs text-slate-500 dark:text-slate-400">
                              <span className="font-semibold text-slate-700 dark:text-slate-300">
                                {selectedTables.length}
                              </span>
                              /{availableTables.length} selected
                            </span>
                            <button
                              type="button"
                              onClick={() => {
                                const allSelected = availableTables.every((t) =>
                                  isTableSelected(t.tablesSelected, selectedTables, data.useCaseDetails)
                                );
                                if (allSelected) {
                                  setSelectedTables([]);
                                  setData((d) => ({
                                    ...d,
                                    useCaseDetails: (d.useCaseDetails || []).filter(
                                      (detail) =>
                                        toServiceType(pickValue(detail, ["serviceType", "selectedService", "service"])) !== "SqlAnalytics"
                                    ),
                                  }));
                                } else {
                                  const allTableNames = availableTables.map((t) => t.tablesSelected);
                                  setSelectedTables(allTableNames);
                                  setData((d) => {
                                    const existing = (d.useCaseDetails || []).filter(
                                      (detail) =>
                                        toServiceType(pickValue(detail, ["serviceType", "selectedService", "service"])) !== "SqlAnalytics"
                                    );
                                    const newEntries = availableTables
                                      .filter((t) => !selectedTables.some((s) => isSameTableName(s, t.tablesSelected)))
                                      .map((t) => ({
                                        selectedService: "SqlAnalytics",
                                        tableOrFileName: t.tablesSelected,
                                        selectedFields: getFieldsForTable(t.tablesSelected),
                                      }));
                                    return { ...d, useCaseDetails: [...existing, ...newEntries] };
                                  });
                                }
                              }}
                              className="text-xs font-semibold text-amber-500 hover:text-amber-600 transition-colors"
                            >
                              {availableTables.every((t) =>
                                isTableSelected(t.tablesSelected, selectedTables, data.useCaseDetails)
                              ) ? "Deselect All" : "Select All"}
                            </button>
                          </div>

                          {availableTables.map((table, index) => (
                            <label
                              key={index}
                              className="flex items-center gap-3 p-3 rounded-xl border border-slate-200 dark:border-slate-700 hover:border-amber-400 dark:hover:border-amber-500 bg-slate-50 dark:bg-slate-900/30 cursor-pointer transition-colors group"
                            >
                              <input
                                type="checkbox"
                                checked={
                                  Array.isArray(selectedTables) &&
                                  isTableSelected(table.tablesSelected, selectedTables, data.useCaseDetails)
                                }
                                onChange={() => toggleTable(table)}
                                className="w-4 h-4 text-amber-500 focus:ring-amber-500 border-slate-300 rounded"
                              />
                              <span className="text-sm font-medium text-slate-700 dark:text-slate-300 group-hover:text-amber-700 dark:group-hover:text-amber-500 transition-colors font-mono">
                                {table.tablesSelected}
                              </span>
                            </label>
                          ))}
                        </>
                      ) : (
                        <p className="text-sm text-slate-500 col-span-2">No SQL tables connected for this company.</p>
                      )}
                    </div>
                    <p className="text-xs text-slate-500 dark:text-slate-400 mt-3">
                      Columns are auto-included for selected tables.
                    </p>
                  </div>

                  {/* ── PDF Files ── */}
                  <div>
                    <h4 className="text-sm font-bold text-slate-800 dark:text-slate-200 mb-4 pb-2 border-b border-slate-100 dark:border-slate-700 flex items-center gap-2 mt-8">
                      <FileText size={16} className="text-[#36a852]" />
                      Accessible Knowledge Base (PDFs)
                    </h4>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {availableFiles.length > 0 ? (
                        <>
                          <div className="col-span-2">
                            <label className={labelCls}>
                              File Analytics Prompt / Instructions <span className="text-red-500">*</span>
                            </label>
                            <textarea
                              rows={4}
                              placeholder="e.g. You are a document retrieval assistant..."
                              value={data.filePromptConfiguration}
                              onChange={(e) => setData((d) => ({ ...d, filePromptConfiguration: e.target.value }))}
                              className={inputCls + " resize-y"}
                            />
                          </div>

                          {/* Select All row */}
                          <div className="col-span-2 flex items-center justify-between px-1">
                            <span className="text-xs text-slate-500 dark:text-slate-400">
                              <span className="font-semibold text-slate-700 dark:text-slate-300">
                                {availableFiles.filter((f) => isFileSelected(f, data.useCaseDetails || [])).length}
                              </span>
                              /{availableFiles.length} selected
                            </span>
                            <button
                              type="button"
                              onClick={() => {
                                const allSelected = availableFiles.every((f) =>
                                  isFileSelected(f, data.useCaseDetails || [])
                                );
                                if (allSelected) {
                                  setData((d) => ({
                                    ...d,
                                    useCaseDetails: (d.useCaseDetails || []).filter(
                                      (detail) =>
                                        toServiceType(pickValue(detail, ["serviceType", "selectedService", "service"])) !== "FileAnalytics"
                                    ),
                                  }));
                                } else {
                                  const toAdd = availableFiles
                                    .filter((f) => !isFileSelected(f, data.useCaseDetails || []))
                                    .map((f) => ({
                                      selectedService: "FileAnalytics",
                                      tableOrFileName: f.fileName,
                                      selectedFields: f.filePath,
                                    }));
                                  setData((d) => ({
                                    ...d,
                                    useCaseDetails: [...(d.useCaseDetails || []), ...toAdd],
                                  }));
                                }
                              }}
                              className="text-xs font-semibold text-[#36a852] hover:text-green-600 transition-colors"
                            >
                              {availableFiles.every((f) => isFileSelected(f, data.useCaseDetails || []))
                                ? "Deselect All"
                                : "Select All"}
                            </button>
                          </div>

                          {availableFiles.map((file, index) => (
                            <label
                              key={index}
                              className="flex items-center gap-3 p-3 rounded-xl border border-slate-200 dark:border-slate-700 hover:border-[#36a852] bg-slate-50 dark:bg-slate-900/30 cursor-pointer transition-colors group"
                            >
                              <input
                                type="checkbox"
                                checked={Array.isArray(data.useCaseDetails) && isFileSelected(file, data.useCaseDetails)}
                                onChange={() => toggleFile(file)}
                                className="w-4 h-4 text-[#36a852] focus:ring-[#36a852] border-slate-300 rounded"
                              />
                              <span className="text-sm font-medium text-slate-700 dark:text-slate-300 group-hover:text-[#36a852] transition-colors truncate">
                                {file.fileName}
                              </span>
                            </label>
                          ))}
                        </>
                      ) : (
                        <p className="text-sm text-slate-500 col-span-2">No files uploaded for this company.</p>
                      )}
                    </div>
                  </div>

                  {/* ── Audio / Video Media ── */}
                  <div>
                    <h4 className="text-sm font-bold text-slate-800 dark:text-slate-200 mb-4 pb-2 border-b border-slate-100 dark:border-slate-700 flex items-center gap-2 mt-8">
                      <Video size={16} className="text-purple-500" />
                      Accessible Audio & Video Files
                    </h4>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {availableMedia.length > 0 ? (
                        <>
                          <div className="col-span-2">
                            <label className={labelCls}>
                              Media Analytics Prompt / Instructions <span className="text-red-500">*</span>
                            </label>
                            <textarea
                              rows={4}
                              placeholder="e.g. You are a media analysis assistant. Transcribe and analyze audio/video content..."
                              value={data.mediaPromptConfiguration}
                              onChange={(e) => setData((d) => ({ ...d, mediaPromptConfiguration: e.target.value }))}
                              className={inputCls + " resize-y"}
                            />
                          </div>

                          {/* Select All row */}
                          <div className="col-span-2 flex items-center justify-between px-1">
                            <span className="text-xs text-slate-500 dark:text-slate-400">
                              <span className="font-semibold text-slate-700 dark:text-slate-300">
                                {availableMedia.filter((m) => isMediaSelected(m, data.useCaseDetails || [])).length}
                              </span>
                              /{availableMedia.length} selected
                            </span>
                            <button
                              type="button"
                              onClick={() => {
                                const allSelected = availableMedia.every((m) =>
                                  isMediaSelected(m, data.useCaseDetails || [])
                                );
                                if (allSelected) {
                                  setData((d) => ({
                                    ...d,
                                    useCaseDetails: (d.useCaseDetails || []).filter(
                                      (detail) =>
                                        toServiceType(pickValue(detail, ["serviceType", "selectedService", "service"])) !== "MediaAnalytics"
                                    ),
                                  }));
                                } else {
                                  const toAdd = availableMedia
                                    .filter((m) => !isMediaSelected(m, data.useCaseDetails || []))
                                    .map((m) => ({
                                      selectedService: "MediaAnalytics",
                                      tableOrFileName: String(m.originalFileName || m.fileName || m.name || "").trim(),
                                      selectedFields: m.filePath || "",
                                      mediaId: m.id,
                                      mediaType: m.fileType,
                                    }));
                                  setData((d) => ({
                                    ...d,
                                    useCaseDetails: [...(d.useCaseDetails || []), ...toAdd],
                                  }));
                                }
                              }}
                              className="text-xs font-semibold text-purple-500 hover:text-purple-600 transition-colors"
                            >
                              {availableMedia.every((m) => isMediaSelected(m, data.useCaseDetails || []))
                                ? "Deselect All"
                                : "Select All"}
                            </button>
                          </div>

                          {availableMedia.map((media, index) => {
                            const isVideo = media.fileType === "Video" || media.contentType?.startsWith("video/");
                            const mediaName = media.originalFileName || media.fileName || media.name;
                            const isChecked = Array.isArray(data.useCaseDetails) && isMediaSelected(media, data.useCaseDetails);
                            return (
                              <label
                                key={media.id || index}
                                className="flex items-center gap-3 p-3 rounded-xl border border-slate-200 dark:border-slate-700 hover:border-purple-500 bg-slate-50 dark:bg-slate-900/30 cursor-pointer transition-colors group"
                              >
                                <input
                                  type="checkbox"
                                  checked={isChecked}
                                  onChange={() => toggleMedia(media)}
                                  className="w-4 h-4 text-purple-500 focus:ring-purple-500 border-slate-300 rounded"
                                />
                                {isVideo ? (
                                  <Video size={16} className="text-purple-500 flex-shrink-0" />
                                ) : (
                                  <Music size={16} className="text-purple-500 flex-shrink-0" />
                                )}
                                <span className="text-sm font-medium text-slate-700 dark:text-slate-300 group-hover:text-purple-500 transition-colors truncate">
                                  {mediaName}
                                </span>
                                <span className="text-xs text-slate-400 flex-shrink-0 ml-auto">
                                  {media.fileType || (isVideo ? "Video" : "Audio")}
                                </span>
                              </label>
                            );
                          })}
                        </>
                      ) : (
                        <p className="text-sm text-slate-500 col-span-2">No audio/video files uploaded for this company.</p>
                      )}
                    </div>
                    <p className="text-xs text-slate-500 dark:text-slate-400 mt-3">
                      Select media files to enable transcription and sentiment analysis.
                    </p>
                  </div>

                </div>
              )}

              {/* Footer */}
              <div className="absolute bottom-0 left-0 right-0 p-6 bg-white/90 dark:bg-slate-800/90 backdrop-blur-sm border-t border-slate-100 dark:border-slate-700 flex items-center justify-between">
                <button
                  onClick={() => setStep(1)}
                  className="text-slate-500 dark:text-slate-400 hover:text-slate-800 dark:hover:text-white px-6 py-2.5 rounded-full font-medium transition-colors flex items-center gap-2 text-sm"
                >
                  <ArrowLeft size={16} /> Back
                </button>

                <button
                  onClick={() => {
                    const hasSql =
                      Array.isArray(data.useCaseDetails) &&
                      data.useCaseDetails.some(
                        (d) => d.selectedService === "SqlAnalytics",
                      );
                    const hasFile =
                      Array.isArray(data.useCaseDetails) &&
                      data.useCaseDetails.some(
                        (d) => d.selectedService === "FileAnalytics",
                      );
                    const hasMedia =
                      Array.isArray(data.useCaseDetails) &&
                      data.useCaseDetails.some(
                        (d) => d.selectedService === "MediaAnalytics",
                      );

                    if (!hasSql && !hasFile && !hasMedia) {
                      showToast(
                        "Please select at least one SQL table, PDF file, or Media file",
                        "error",
                      );
                      return;
                    }
                    if (
                      hasSql &&
                      !String(data.sqlPromptConfiguration || "").trim()
                    ) {
                      showToast(
                        "SQL Prompt Configuration is required when SQL tables are selected",
                        "error",
                      );
                      return;
                    }
                    if (
                      hasFile &&
                      !String(data.filePromptConfiguration || "").trim()
                    ) {
                      showToast(
                        "File Prompt Configuration is required when files are selected",
                        "error",
                      );
                      return;
                    }
                    if (
                      hasMedia &&
                      !String(data.mediaPromptConfiguration || "").trim()
                    ) {
                      showToast(
                        "Media Prompt Configuration is required when media files are selected",
                        "error",
                      );
                      return;
                    }
                    onFinish(data);
                  }}
                  className="bg-[#1d57a5] hover:bg-[#164280] text-white px-8 py-3 rounded-full font-bold transition-all shadow-md hover:shadow-lg hover:-translate-y-0.5 flex items-center gap-2"
                >
                  Create Workspace
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
      {toast.visible && (
        <div className="fixed top-6 right-6 z-[9999] bg-slate-900 dark:bg-white text-white dark:text-slate-900 px-6 py-4 rounded-2xl shadow-2xl flex items-center gap-3 animate-in slide-in-from-top-4 duration-300">
          <div
            className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 ${toast.type === "success" ? "bg-[#36a852]" : "bg-red-500"}`}
          >
            {toast.type === "success" ? (
              <Check size={14} strokeWidth={3} />
            ) : (
              <span
                style={{ fontSize: "14px", fontWeight: "bold", lineHeight: 1 }}
              >
                ✕
              </span>
            )}
          </div>
          <span className="text-sm font-semibold">{toast.message}</span>
        </div>
      )}
    </div>
  );
}